import { type ButtonHTMLAttributes, type ReactNode, type Ref } from 'react';
import tinycolor from 'tinycolor2';
import styled, { css, type DefaultTheme } from 'styled-components';

import Spinner from 'components/common/Spinner';
import BaseButton from './BaseButton';

const getSizeStyles = (size: string) => {
  switch (size) {
    case 'small':
      return css`
        height: 24px;
        font-size: 12px;
        padding: 0 8px;
        min-width: 24px;
      `;
    case 'medium':
      return css`
        font-size: 14px;
        height: 32px;
        padding: 4px 8px;
      `;
    case 'large':
    default:
      return css`
        font-size: 16px;
        height: 36px;
        padding: 8px 8px;
      `;
  }
};

type Theme = DefaultTheme;

type StyledButtonTransientProps = {
  $size?: string;
  $variant?: string;
  $color?: string;
  $width?: string;
  $hasLeftIcon?: boolean;
  $hasRightIcon?: boolean;
  $active?: boolean;
  $isDisabled?: boolean;
  children?: ReactNode;
  [key: string]: unknown;
};

// specific variant overwrites
const getVariantStyles = (theme: Theme, variant: string, color: string) => {
  const isLight = theme.name === 'Light';
  switch (variant) {
    case 'outlined':
      if (color === 'secondary') {
        return css`
          color: ${({ theme }) => theme.palette.secondary[isLight ? 2 : 3]};
          border-color: ${({ theme }) => theme.palette.secondary[isLight ? 2 : 3]};
          background-color: transparent;
        `;
      }
      if (color === 'danger') {
        return css`
          color: ${({ theme }) => theme.palette.danger[isLight ? 2 : 3]};
          border-color: ${({ theme }) => theme.palette.danger[isLight ? 2 : 3]};
          background-color: transparent;
        `;
      }
      return css`
        background-color: transparent;
      `;
    case 'text':
      return css`
        border-color: transparent;
        background-color: transparent;
      `;
    case 'accented':
      if (color === 'secondary') {
        return css`
          color: ${({ theme }) => theme.palette.secondary[isLight ? 2 : 3]};
          border-color: ${({ theme }) => theme.colors.border.default};
          background-color: transparent;
        `;
      }
      return css`
        border-color: ${({ theme }) => theme.colors.border.default};
        background-color: transparent;
      `;
    case 'contained':
      if (color === 'default') {
        return css`
          border-color: ${({ theme }) => theme.colors.text.primary};
          color: ${({ theme }) => theme.colors.text.primary};
          background-color: 'transparent';
        `;
      }
      if (color === 'secondary') {
        return css`
          border-color: ${({ theme }) => theme.palette.secondary[isLight ? 2 : 0]};
          background-color: ${({ theme }) => theme.palette.secondary[isLight ? 2 : 0]};
        `;
      }
      if (color === 'danger') {
        return css`
          border-color: ${({ theme }) => theme.palette.danger[isLight ? 2 : 1]};
          background-color: ${({ theme }) => theme.palette.danger[isLight ? 2 : 1]};
        `;
      }
    default:
      return css`
        color: ${({ theme }) => theme.palette.white};
      `;
  }
};

const _getColorFromKey = (theme: Theme, colorKey: string) => {
  const isLight = theme.name === 'Light';
  const colors: Record<string, string> = {
    primary: theme.palette.primary[2],
    secondary: theme.palette.secondary[2],
    danger: theme.palette.danger[2],
    success: theme.palette.success[2],
    warning: theme.palette.alert[isLight ? 0 : 1],
  };
  return colors[colorKey];
};

const getColorStyles = (theme: Theme, color: string) => {
  const baseColor = _getColorFromKey(theme, color);
  if (color === 'default') {
    return css`
      color: ${({ theme }) => theme.colors.text.primary};
      border-color: ${({ theme }) => theme.colors.text.primary};
      background-color: transparent;
    `;
  }
  if (baseColor === undefined) {
    // color is defined by a custom string
    return css<StyledButtonTransientProps>`
      color: ${({ theme, $variant }) => ($variant === 'contained' ? theme.palette.white : color)};
      border-color: ${color};
      background-color: ${color};
    `;
  }
  return css<StyledButtonTransientProps>`
    color: ${({ theme, $variant }) => ($variant === 'contained' ? theme.palette.white : baseColor)};
    border-color: ${baseColor};
    background-color: ${baseColor};
  `;
};

const getHoverStyles = (theme: Theme, variant: string, color: string) => {
  const baseColor = _getColorFromKey(theme, color) || color;
  const rgba = tinycolor(baseColor).toRgb();
  if (variant === 'contained' && color !== 'default') {
    const rgba = tinycolor(baseColor).darken(8).toRgb();
    return css`
      &:hover,
      &:focus {
        box-shadow: inset 0 0 999em rgba(${rgba.r}, ${rgba.g}, ${rgba.b});
      }
    `;
  }
  if (color === 'default') {
    return css`
      &:hover,
      &:focus {
        background-color: ${({ theme }) =>
          theme.name === 'Dark'
            ? tinycolor(theme.colors.background.default).brighten(10).toString()
            : tinycolor(theme.colors.background.default).darken(2).setAlpha(0.1).toString()};
      }
    `;
  }
  return css`
    &:hover,
    &:focus {
      box-shadow: inset 0 0 999em rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, 0.1);
    }
  `;
};

const StyledButton = styled(BaseButton)<StyledButtonTransientProps>`
  border: 1px solid;
  ${({ $size }) => getSizeStyles($size!)};
  ${({ theme, $color }) => getColorStyles(theme, $color!)};
  ${({ theme, $variant, $color }) => getVariantStyles(theme, $variant!, $color!)};
  ${({ theme, $variant, $color }) => getHoverStyles(theme, $variant!, $color!)};
  font-weight: 400;
  width: ${({ $width }) => ($width ? $width : 'auto')};

  ${({ $hasLeftIcon }) =>
    $hasLeftIcon &&
    css<StyledButtonTransientProps>`
      svg {
        margin-right: ${({ $size }) => ($size === 'large' ? '8px' : '4px')};
      }
    `}

  ${({ $hasRightIcon }) =>
    $hasRightIcon &&
    css<StyledButtonTransientProps>`
      svg {
        margin-left: ${({ $size }) => ($size === 'large' ? '8px' : '4px')};
      }
    `}

  &:disabled {
    pointer-events: unset;
    cursor: not-allowed;
    opacity: 0.8;
  }
`;

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  isLoading?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'contained' | 'outlined' | 'accented' | 'text';
  color?: string;
  width?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  active?: boolean;
  isDisabled?: boolean;
  'data-test-id'?: string;
  ref?: Ref<HTMLButtonElement>;
};

const Button = ({
  isLoading = false,
  children,
  size = 'large',
  variant = 'outlined',
  width,
  color = 'default',
  leftIcon = null,
  rightIcon = null,
  active,
  isDisabled,
  ref,
  ...props
}: ButtonProps) => {
  if (isLoading) {
    return (
      <StyledButton
        {...props}
        ref={ref}
        $size={size}
        $variant={variant}
        $color={color}
        $width={width}
        $hasLeftIcon={!!leftIcon}
        $hasRightIcon={!!rightIcon}
        $active={active}
        $isDisabled={isDisabled}
      >
        <Spinner color="currentColor" />
      </StyledButton>
    );
  }
  return (
    <StyledButton
      {...props}
      ref={ref}
      $size={size}
      $variant={variant}
      $color={color}
      $width={width}
      $hasLeftIcon={!!leftIcon}
      $hasRightIcon={!!rightIcon}
      $active={active}
      $isDisabled={isDisabled}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </StyledButton>
  );
};

export default Button;
